import {
    asyncScheduler,
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    interval,
    merge,
    Observable,
    of,
    scan,
    scheduled,
    Subject,
    switchMap,
    throttleTime,
    catchError,
    filter,
    map,
    lastValueFrom, takeUntil,
    tap,
} from 'rxjs';
import flowplayer from '@flowplayer/player';
import type { Player } from '@flowplayer/player';
import FASPlugin from '@flowplayer/player/plugins/fas';
import type { FASPlayer } from '@flowplayer/player/plugins/fas';
import HLSPlugin from '@flowplayer/player/plugins/hls';
import { GetHlsFileManifestArgs, GetHlsManifestArgs, HlsService } from '../backend-api/hls.service';
import { MediaFilter } from '../backend-api/media-filter.model';
import { AudioTrack } from '../backend-api/audiotrack.model';
import { HlsManifestInfo } from '../backend-api/hls-manifest-info.model';
import { MediaTimeMapping } from '../shared/media-time-mapping.model';
import { Duration, fromMilliseconds, fromSeconds } from '../../shared/duration';
import { formatTimeSpan, parseTimeSpan } from '../../shared/timespan';
import { MediaInformation } from './media-information.interface';
import { MediaCutInfo } from '../shared/media-cut-info';
import { FileInformation } from './file-information.interface';
import { NotificationService } from 'src/app/shared/services/notification.service';

const fp = flowplayer(FASPlugin, HLSPlugin);

type FlowPlayer = Player & FASPlayer;

export class HlsBackendFlowPlayer {
    private _playing$ = new BehaviorSubject(false);
    playing$ = this._playing$.asObservable();

    public seeking$!: Observable<boolean>; // initialized in "init"

    private _playedToEnd$ = new Subject();

    /** Emitted when the current media is played to the end */
    public playedToEnd$: Observable<unknown> = this._playedToEnd$.asObservable();

    private _mediaLoaded$ = new Subject<MediaCutInfo>();
    mediaLoaded$ = this._mediaLoaded$.asObservable();

    currentLinearTime$!: Observable<Duration>; // initialized in "init"

    playerState: boolean  = false;

    private _mediaInfo: MediaInformation | null = null;
    private _fileInfo: FileInformation | null = null;
    private _mediaCutInfo: MediaCutInfo | null = null;
    private _manifestInfo: ManifestInfo = defaultManifestInfo;

    private _playerTime$!: Observable<Duration>; // initialized in "init"
    private _seekSeq = 0;
    private _seekRequest$ = new Subject<SeekRequestType>();
    private _seekCompleted$ = new Subject<number>();

    private _player!: FlowPlayer; // initialized in "init" via fp()
    private _pendingSeekSeq: number | null = null; // seq to complete via DATA event after new manifest load
    private _destroyed$: Subject<void> = new Subject<void>();

    private _manifestRequested$: Subject<ManifestRequestInfo> = new Subject<ManifestRequestInfo>();
    manifestRequested$ = this._manifestRequested$.asObservable();

    private _volumeChanged$: BehaviorSubject<VolumeInfo | null> = new BehaviorSubject<VolumeInfo | null>(null);
    volumeChanged$ = this._volumeChanged$.asObservable();

    private readonly offsetFromOut = fromSeconds(25); 
    private readonly extraTime = fromSeconds(10);

    constructor(
      private readonly videoService: HlsService,
      private readonly notificationService: NotificationService
    ) {
        //solves issue when multiple media cuts are loaded at the same time
        this._seekRequest$.pipe(takeUntil(this._destroyed$)).subscribe(data => {
            if (!data.initial && this.playerState) {
                setTimeout(() => this.play(), 800);
            }
        })
    }

    async init(flowPlayerElementSelector: string, enableControls: boolean = true) {
        const playerOptions = await this.getPlayerOptions(enableControls);
        const player = (this._player = fp(flowPlayerElementSelector, playerOptions) as FlowPlayer);

        player.on(flowplayer.events.PLAYING, () => {
            this._playing$.next(true);
        });

        player.on(flowplayer.events.PAUSE, () => {
            this._playing$.next(false);
        });

        player.on(flowplayer.events.ERROR, (e: any) => {
            console.error('error on player:', e);
            this.emitSeekCompletedOfPendingSeek();
        });

        player.on(flowplayer.events.ENDED, () => {
            console.log('player completed playback');
            this._playedToEnd$.next(true);
        });

        player.on(flowplayer.events.LOAD, () => {
          console.log(`player event load @ ${player.currentTime}`);
        });
        player.on(flowplayer.events.LOAD_START, () => {
          console.log(`player started loading @ ${player.currentTime}`);
        });
        player.on(flowplayer.events.DATA, () => {
            console.log(`player loaded first frame @ ${player.currentTime}`);
        });
  
        player.on(flowplayer.events.SEEKING, () => {
            console.log(`player seeking @ ${player.currentTime}`);
        });

        player.on(flowplayer.events.SEEKED, () => {
            console.log(`player seeked @ ${player.currentTime}`);
            // For new manifest loads, defer seekCompleted until the first frame is actually
            // available so the "seeking" freeze is not released while currentTime is still 0.
            this.emitSeekCompletedOfPendingSeek();
        });


        player.on(flowplayer.events.VOLUME_CHANGE, () => {
            const volumeInfo = this.getVolumeInfo();
            // console.log('on volumechange', volumeInfo);
            this._volumeChanged$.next(volumeInfo);
        });

        this._playerTime$ = interval(50).pipe(
            map(_ => this._player.currentTime),
            distinctUntilChanged(),
            map(pt => {
                // current player time restricted to <= ltcOut in milliseconds
                const ltc = this._manifestInfo.ltcOffset.add(fromSeconds(pt));
                const ltcOut = this._mediaCutInfo?.ltcOut || zeroTime;
                return ltc.asMilliseconds() <= ltcOut.asMilliseconds() ? ltc : ltcOut;
            })
        );

        // this._seekCompleted$.pipe(takeUntil(this._destroyed$))
        //     .subscribe(seq => console.log(`seek ${seq} completed`));

        const time$ = merge(
            this._playerTime$.pipe(map(t => ({ type: TimeTrackingEventType.PLAYER_TIME, time: t }))),
            this._seekRequest$.pipe(map(x => ({ type: TimeTrackingEventType.SEEK_TIME, time: x.time }))),
            this._seekCompleted$.pipe(map(_ => ({ type: TimeTrackingEventType.SEEK_COMPLETED, time: null })))
        ).pipe(
            scan(
                function (acc, e) {
                    switch (e.type) {
                        case TimeTrackingEventType.PLAYER_TIME:
                            return { seeking: acc.seeking, time: acc.seeking ? acc.time : e.time! };
                        case TimeTrackingEventType.SEEK_TIME:
                            return { seeking: true, time: e.time || zeroTime };
                        case TimeTrackingEventType.SEEK_COMPLETED:
                            return { seeking: false, time: acc.time };
                    }
                },
                { seeking: false, time: zeroTime }
            )
        );

        this.currentLinearTime$ = time$.pipe(
          map(x => x.time)
        );

        this.seeking$ = time$.pipe(
            map(x => x.seeking),
            distinctUntilChanged()
        );

        this.setupLoadManifest();
        this.setupLoadManifestExtension();

        this._volumeChanged$.next(this.getVolumeInfo());
    }

    private emitSeekCompletedOfPendingSeek() {
        if (this._pendingSeekSeq !== null) {
            this._seekCompleted$.next(this._pendingSeekSeq);
            this._pendingSeekSeq = null;
        }
    }

    private getVolumeInfo(): VolumeInfo {
        return { muted: this.muted, volume: this.volume };
    }

    load(media: MediaInformation, autoStart: boolean) {
        console.log('load', media, autoStart);

        if (this._mediaInfo && isSameMediaSelection(this._mediaInfo, media)) {
            this._mediaLoaded$.next(this._mediaCutInfo!);
            return;
        }

        this._fileInfo = null;
        this._mediaInfo = {...media};
        this._mediaCutInfo = null;
        this._manifestInfo = defaultManifestInfo;

        if (autoStart) {
            this._playAfterSeek = 'yes';
        }
        this._playbackRate = 1;
        this._seekRequest$.next({ time: null, seq: this._seekSeq++, initial: true });
    }

    loadFile(file: FileInformation, autoStart: boolean) {
        console.log('loadFile', file, autoStart);

        this._mediaInfo = null;
        this._fileInfo = {...file};
        this._mediaCutInfo = null;
        this._manifestInfo = defaultManifestInfo;
        
        if (autoStart) {
            this._playAfterSeek = 'yes';
        }
        this._playbackRate = 1;
        this._seekRequest$.next({ time: null, seq: this._seekSeq++, initial: true });
    }
  
    destroy() {
        // destroy see: https://docs.flowplayer.com/player/player-api#methods
        this._player.destroy();

        this._destroyed$.next();
        this._destroyed$.complete();
    }

    isPlaying(): boolean {
        return this._playing$.value;
    }

    isPaused(): boolean {
        return !this._playing$.value;
    }

    hasFinishedPlaying(): boolean {
      return this._player.ended;
    }

    play() {
        this._player.togglePlay(true);
    }

    pause() {
        this._player.togglePlay(false);
    }

    frameForward() {
        this.pause();
        this._player.fas.jump(1);
    }

    frameBackward() {
      this.pause();
      this._player.fas.jump(-1);
  }

    private _dataLoadingPaused$ = new BehaviorSubject(false);

    get isDataLoadingPaused(): boolean {
        return this._dataLoadingPaused$.value;
    }

    pauseDataLoading(s: boolean) {
        if (this._dataLoadingPaused$.value !== s) {
            this._dataLoadingPaused$.next(s);
        }
    }

    private _playAfterSeek = '';
    private _playbackRate: number = 1;

    /**
     * Sets the current player time
     * @param ltc New player time as linear time code w.r.t. currently loaded media.
     */
    setCurrentLinearTime(ltc: Duration) {
        if (!this._mediaCutInfo) return;

        // fix ltc to 1 ms
        ltc = fromMilliseconds(Math.floor(ltc.asMilliseconds()));

        if (ltc.asMilliseconds() < 0) {
            ltc = fromMilliseconds(0);
        } else if (ltc.asMilliseconds() > this._mediaCutInfo.ltcOut.asMilliseconds()) {
            ltc = this._mediaCutInfo.ltcOut;
        }

        this._playAfterSeek = this._playAfterSeek || (this.isPlaying() ? 'yes' : 'no');
        this._playbackRate = this._player.playbackRate;

        this.pause();

        console.log('setCurrentLinearTime', ltc.asSeconds(), this._playAfterSeek);
        this._seekRequest$.next({ time: ltc, seq: this._seekSeq++ });
    }

    toggleMute() {
        this._player.toggleMute();
    }

    get muted(): boolean {
        return this._player.muted;
    }

    set muted(val: boolean) {
        this._player.muted = val;
    }

    set playbackRate(val: number) {
        this._player.playbackRate = val;
    }

    get playbackRate(): number {
        return this._player.playbackRate;
    }

    get volume(): number {
        return this._player.volume;
    }

    set volume(val: number) {
        this._player.volume = val;
    }

    set aspectRatio(val: string | undefined) {
        this._player.setOpts({
            ratio: val,
        });
    }

  /*
    get currentTime(): number {
        return this._player.currentTime;
    }

    set currentTime(val: number) {
      this._player.currentTime = val;
    }
*/

    get aspectRatio(): string | undefined {
        const ratio = this._player.opts.ratio;
        return typeof ratio === 'number' ? `${ratio}` : ratio;
    }

    private async getPlayerOptions(enableControls: boolean) {
        let token = undefined;
        try {
            const token$ = this.videoService.getLicenceToken();
            token = await lastValueFrom(token$);
        } catch (err) {
            console.error('Error fetching flow player token', err);
        }

        return {
            autoplay: false,
            controls: enableControls,
            hls: {
                native: false,
                nudgeMaxRetry: 100,
                // debug: true,
            },
            live: false,
            seekable: false,
            ratio: '16:9',
            token,
        };
    }

    private setupLoadManifest() {
        combineLatest([this._seekRequest$, this._dataLoadingPaused$])
            .pipe(
                filter(([_, dataLoadingPaused]) => !dataLoadingPaused),
                switchMap(([seekRequest, _]) => {
                    const m = this._manifestInfo;
                    // console.log(`initial = ${seekRequest.initial}, time = ${seekRequest.time?.asSeconds()}, mainifest ltc = [${m.ltcStart.asSeconds()}, ${m.ltcEnd.asSeconds()}]`);
                    const seekMs = seekRequest.time?.asMilliseconds();
                    if (seekMs && m.ltcStart.asMilliseconds() <= seekMs && seekMs < m.ltcEnd.asMilliseconds() - 200) {
                        // console.log('-> in current manifest');
                        // we use scheduled so that result only appears asynchronously
                        // otherwise seekCompleted event can arrive before the corresponding seekTime event in
                        // the currentLinearTime$ "scan".
                        const mediaCutId = this._mediaInfo?.selectedMediaCutId || this._fileInfo!.fileName;
                        return scheduled(
                            [{ manifest: m.manifest, seekRequest, error: undefined, mediaCutId }],
                            asyncScheduler
                        );
                    } else {
                        // console.log(`load new manifest: seekRequest.time = ${seekRequest.time?.asSeconds()}, seekRequest.initial = ${seekRequest.initial}`);
                        return this.loadManifest$(seekRequest);
                    }
                }),
                takeUntil(this._destroyed$)
            )
            .subscribe(x => {
                if (x.error) {
                    console.error('loadManifest$', x.error);
                    this._seekCompleted$.next(x.seekRequest.seq);
                    return;
                }

                console.log('processing seekRequest', x.seekRequest);

                const manifest = x.manifest!;

                if (x.seekRequest.initial) {
                    this._mediaCutInfo = parseMediaCutInfo(x.mediaCutId, manifest);
                    this._player.setOpts({
                        // frame-accurate seeking plugin, see https://docs.flowplayer.com/plugins/fas
                        fas: { frame_rate: this._mediaCutInfo!.frameRate },
                    } as any);
                }

                const newManifest = manifest !== this._manifestInfo.manifest;
                if (newManifest) {
                    this._manifestInfo = parseManifestInfo(manifest);
                    const url = this.videoService.getManifestUrl(manifest.url);

                    let ct: Duration;
                    if (x.seekRequest.time) {
                        ct = x.seekRequest.time.subtract(this._manifestInfo.ltcOffset);
                    } else {
                        const offset = this._mediaInfo?.startOffset || this._fileInfo?.startOffset || fromMilliseconds(0);
                        ct = offset.subtract(this._manifestInfo.ltcOffset)
                    }

                    const startTime = ct.asSeconds() || Number.EPSILON; // flowplayer does not start at 0 when using hls plugin, so we set it to a very small value larger than 0 in that case
                    this._player.setOpts({ start_time: startTime });

                    this._player.setSrc(url);

                    console.log(`start_time: ${startTime}, seekRequest.time = ${x.seekRequest.time?.asSeconds()}, seekRequest.initial = ${x.seekRequest.initial}`);
                    console.log(`set _player source to ${url}`);

                    this._manifestRequested$.next({
                      action: 'get',
                      manifestId: manifest.manifestId,
                      vtcIn: fromSeconds(parseFloat(manifest.timingInfo.vtcIn)),
                      vtcStart: fromSeconds(parseFloat(manifest.timingInfo.vtcStart)),
                      vtcEnd: fromSeconds(parseFloat(manifest.timingInfo.vtcEnd))
                    });
                } else {
                    const ct = x.seekRequest.time!.subtract(this._manifestInfo.ltcOffset);
                    const seekTime = ct.asSeconds() || Number.EPSILON; // flowplayer does not start at 0 when using hls plugin, so we set it to a very small value larger than 0 in that case
                    this._player.currentTime = seekTime;
                    console.log(`manifest already loaded, just seek to new position ${seekTime}`);
                }

                this._player.playbackRate = this._playbackRate;

                // console.log(`processing this._playAfterSeek ${this._playAfterSeek}`);
                if (this._playAfterSeek === 'yes') {
                    this.play();
                }
                this._playAfterSeek = '';

                if (newManifest) {
                    // seekCompleted is deferred to the SEEKED event handler so the "seeking" freeze
                    // is not released while currentTime is still 0 during the load.
                    this._pendingSeekSeq = x.seekRequest.seq;
                } else {
                    this._seekCompleted$.next(x.seekRequest.seq);
                }

                if (x.seekRequest.initial) {
                    this._mediaLoaded$.next(this._mediaCutInfo!);
                }
            });
    }

    private setupLoadManifestExtension() {
        const extendManifest$ = this._playerTime$.pipe(
            /* only check once every 500 ms to load manifest extension...*/
            throttleTime(500, asyncScheduler, { leading: false, trailing: true }),
            distinctUntilChanged((x, y) => x.asMilliseconds() === y.asMilliseconds()),
            filter(_ => !!this._mediaCutInfo && !!this._manifestInfo.manifest),
            map(ltc => {
                const mc = this._mediaCutInfo!;
                const m = this._manifestInfo;

                const canExtendManifest = m.ltcEnd.asMilliseconds() < mc.ltcOut.asMilliseconds();
                const currentLtcInSeconds = ltc.asSeconds();
                const isCloseToEndPosition = currentLtcInSeconds + 10 >= m.ltcEnd.asSeconds();

                return canExtendManifest && isCloseToEndPosition
                    ? {
                          manifestId: this._manifestInfo.manifest!.manifestId,
                          ltcEnd: m.ltcEnd.asMilliseconds(),
                      }
                    : null;
            }),
            filter(x => x !== null),
            distinctUntilChanged((x, y) => x!.ltcEnd === y!.ltcEnd && x!.manifestId === y!.manifestId),
            map(x => x!.manifestId)
        );

        combineLatest([extendManifest$, this.seeking$])
            .pipe(
                switchMap(([manifestId, seeking]) => {
                    if (seeking) {
                        return of({ manifest: null, error: undefined });
                    } else {
                        return this.loadManifestExtension$(manifestId!);
                    }
                })
            )
            .subscribe(x => {
                if (x.error) {
                    console.error(x.error);
                    return;
                }

                if (x.manifest) {
                    if (x.manifest.manifestId === this._manifestInfo.manifest?.manifestId) {
                        this._manifestInfo = parseManifestInfo(x.manifest);
                        this._manifestRequested$.next({
                          action: 'extend',
                          manifestId: x.manifest.manifestId,
                          vtcIn: fromSeconds(parseFloat(x.manifest.timingInfo.vtcIn)),
                          vtcStart: fromSeconds(parseFloat(x.manifest.timingInfo.vtcStart)),
                          vtcEnd: fromSeconds(parseFloat(x.manifest.timingInfo.vtcEnd)) 
                        });
                    }
                    // else case might happen when the user seeks in between extension loading and receiving...
                }
            });
    }

    private loadManifest$(seekRequest: SeekRequestType): Observable<{
        manifest: HlsManifestInfo | null;
        mediaCutId: string;
        seekRequest: SeekRequestType;
        error?: any;
    }> {
        if (this._mediaInfo) {
            const media = this._mediaInfo!;
            const args: GetHlsManifestArgs = {
                mediaCutId: media.selectedMediaCutId,
                mediaLevel: media.shot
                    ? MediaFilter.Shot
                    : media.item
                        ? MediaFilter.Item
                        : MediaFilter.Program,
                audioTrack: parseAudioTrack(media.selectedAudioTrackId),
            };

            if (!seekRequest.time) {
                // when time === null it is the initial loading of manifest
                if (media.startOffset.asMilliseconds() === 0) {
                    args.itemId = media.item;
                    args.shotId = media.shot;
                    if (args.mediaLevel === MediaFilter.Program) {
                      args.vtcIn = media.startVtc ? formatTimeSpan(media.startVtc) : undefined;
                      args.vtcOut = media.endVtc ? formatTimeSpan(media.endVtc) : undefined;
                    }
                } else {
                    if (media.startVtc) {
                        let offsetInMs = media.startOffset.asMilliseconds() - this.extraTime.asMilliseconds();
                        offsetInMs = Math.max(offsetInMs, 0);
                        args.vtcIn = formatTimeSpan(media.startVtc.add(fromMilliseconds(offsetInMs)));
                    }
                    if (args.mediaLevel === MediaFilter.Program) {
                        args.vtcOut = media.endVtc ? formatTimeSpan(media.endVtc) : undefined;
                    }
                }
            } else {
                // if the manifest was already loaded we give the seekTime as vtcIn
                const mc = this._mediaCutInfo!;
                let vtcInMs = mc.timeCodeMap.ltcToVtc(seekRequest.time).asMilliseconds() - this.extraTime.asMilliseconds();

                // make sure vtcIn is <= mc.vtcOut - 25 sec...
                vtcInMs = Math.min(vtcInMs, mc.vtcOut.asMilliseconds() - this.offsetFromOut.getMilliseconds());

                // make sure vtcIn >= mc.vtcIn
                vtcInMs = Math.max(vtcInMs, mc.vtcIn.asMilliseconds());

                args.vtcIn = formatTimeSpan(fromMilliseconds(vtcInMs));
            }
            return this.videoService.getHlsManifest(args).pipe(
                tap(manifest => console.log(`getHlsManifest: ${manifest.url}, args =`, args)),
                map(manifest => ({ manifest, seekRequest, mediaCutId: args.mediaCutId })),
                catchError(err => {
                    this.notificationService.showToast(`getHlsManifest: Fehler ${err.status}`, err.message, 'error');
                    return of({ error: err, manifest: null, seekRequest, mediaCutId: args.mediaCutId }); }
                )
            );

        } else if (this._fileInfo) {
            const args: GetHlsFileManifestArgs = {
                fileName: this._fileInfo.fileName,
                isAudioFile: this._fileInfo.isAudioFile,
                frameRate: this._fileInfo.frameRate,
                length: formatTimeSpan(this._fileInfo.ltcOut.subtract(this._fileInfo.ltcIn)),
                audioTrack: parseAudioTrack(this._fileInfo.selectedAudioTrackId)
            };

            let ltcInMs = 0;
            if (!seekRequest.time) {
                if (this._fileInfo.startOffset.asMilliseconds() !== 0) {
                    ltcInMs = this._fileInfo.startOffset.asMilliseconds() - this.extraTime.asMilliseconds();
                }
            } else {
                ltcInMs = seekRequest.time.asMilliseconds() - this.extraTime.asMilliseconds();
            }
            ltcInMs = Math.min(ltcInMs, this._fileInfo.ltcOut.asMilliseconds() - this.offsetFromOut.getMilliseconds());
            ltcInMs = Math.max(ltcInMs, 0);
            args.ltcIn = formatTimeSpan(fromMilliseconds(ltcInMs));

            return this.videoService.getHlsFileManifest(args).pipe(
                tap(manifest => console.log(`getHlsFileManifest: ${manifest.url}, args =`, args)),
                map(manifest => ({ manifest, seekRequest, mediaCutId: args.fileName })),
                catchError(err => {
                  this.notificationService.showToast(`getHlsFileManifest: Fehler ${err.status}`, `File: ${args.fileName}`, 'error');
                  return of({ error: err, manifest: null, seekRequest, mediaCutId: args.fileName });}
                )
            );
        }

        return EMPTY;
    }

    private loadManifestExtension$(manifestId: string): Observable<{
        manifest: HlsManifestInfo | null;
        error?: any;
    }> {
        return this.videoService.getExtendHlsManifest(manifestId).pipe(
            map(manifest => ({ manifest })),
            catchError(err => of({ error: err, manifest: null }))
        );
    }

    getStillImage(vtc: Duration): Observable<Blob> {
        if (this._mediaInfo) {
            return this.videoService.getStillImage({
                mediaCutId: this._mediaInfo.selectedMediaCutId,
                mediaLevel: this._mediaInfo.item ? MediaFilter.Item : MediaFilter.Program,
                vtcIn: formatTimeSpan(vtc),
            });
        } else if (this._fileInfo) {
            return this.videoService.getFileStillImage({
                fileName: this._fileInfo.fileName,
                length: formatTimeSpan(this._fileInfo.ltcOut.subtract(this._fileInfo.ltcIn)),
                ltcIn: formatTimeSpan(vtc),
            });
        }

        return EMPTY;
    }
}

function parseManifestInfo(manifest: HlsManifestInfo): ManifestInfo {
    const t = manifest.timingInfo;
    const vtcIn = parseTimeSpan(t.vtcIn);
    const vtcStart = parseTimeSpan(t.vtcStart);
    const vtcEnd = parseTimeSpan(t.vtcEnd);
    const map = new MediaTimeMapping(manifest.ltcMap);
    return {
        manifest,
        ltcStart: map.vtcToLtc(vtcStart),
        ltcEnd: map.vtcToLtc(vtcEnd),
        ltcOffset: vtcStart.subtract(vtcIn),
    };
}

const defaultManifestInfo: ManifestInfo = {
    manifest: null,
    ltcStart: fromMilliseconds(0),
    ltcEnd: fromMilliseconds(0),
    ltcOffset: fromMilliseconds(0),
};

interface ManifestInfo {
    manifest: HlsManifestInfo | null;
    ltcStart: Duration;
    ltcEnd: Duration;
    ltcOffset: Duration;
}

function parseMediaCutInfo(mediaCutId: string, manifest: HlsManifestInfo): MediaCutInfo {
    const t = manifest.timingInfo;
    const vtcIn = parseTimeSpan(t.vtcIn);
    const vtcOut = parseTimeSpan(t.vtcOut);
    const map = new MediaTimeMapping(manifest.ltcMap);
    const ltcIn = map.vtcToLtc(vtcIn);
    const ltcOut = map.vtcToLtc(vtcOut);
    const frameRate = manifest.frameRate;
    return {
        mediaCutId,
        vtcIn,
        ltcIn,
        vtcOut,
        ltcOut,
        length: vtcOut.subtract(vtcIn),
        frameRate: frameRate,
        timeCodeMap: map,
        nextCutId: manifest.nextCut,
    };
}

type SeekRequestType = { time: Duration | null; seq: number; initial?: boolean };

enum TimeTrackingEventType {
    PLAYER_TIME,
    SEEK_TIME,
    SEEK_COMPLETED,
}

const zeroTime = fromMilliseconds(0);

function parseAudioTrack(trackNr: string): AudioTrack {
    switch (trackNr) {
        case 'Stereo1And2':
            return AudioTrack.Stereo12;
        case 'Stereo3And4':
            return AudioTrack.Stereo34;
        case 'Stereo5And6':
            return AudioTrack.Stereo56;
        case 'Stereo7And8':
            return AudioTrack.Stereo78;
        case 'Mono1':
            return AudioTrack.Mono1;
        case 'Mono2':
            return AudioTrack.Mono2;
        case 'Mono3':
            return AudioTrack.Mono3;
        case 'Mono4':
            return AudioTrack.Mono4;
        case 'Mono5':
            return AudioTrack.Mono5;
        case 'Mono6':
            return AudioTrack.Mono6;
        case 'Mono7':
            return AudioTrack.Mono7;
        case 'Mono8':
            return AudioTrack.Mono8;
        default:
            return AudioTrack.Stereo12;
    }
}

function isSameMediaSelection(m1: MediaInformation, m2: MediaInformation) {
    return (
        m1.selectedMediaCutId === m2.selectedMediaCutId &&
        m1.selectedAudioTrackId === m2.selectedAudioTrackId &&
        m1.item === m2.item &&
        m1.shot === m2.shot &&
        m1.startVtc === m2.startVtc &&
        m1.endVtc == m2.endVtc &&
        m1.startOffset === m2.startOffset
    );
}

interface ManifestRequestInfo {
  action: 'get' | 'extend';
  manifestId: string;
  vtcIn: Duration;
  vtcStart: Duration;
  vtcEnd: Duration;
}

interface VolumeInfo {
    muted: boolean;
    volume: number;
}

